// src/landings/components/resources/CategorySidebar.tsx
import { useNavigate } from "react-router-dom";
import { Category } from "../../../data/resources";
import { useRef } from "react";

interface CategorySidebarProps {
  categories: Category[];
  currentCategoryId: string;
  activeArticle: string | null;
}

function CategorySidebar({ categories, currentCategoryId, activeArticle }: CategorySidebarProps) {
  const navigate = useNavigate();
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  // Handle category click without losing scroll position
  const handleCategoryClick = (e: React.MouseEvent<HTMLAnchorElement>, categoryId: string) => {
    e.preventDefault();
    
    // Only navigate if it's a different category
    if (categoryId !== currentCategoryId) {
      // Save current scroll position in sidebar
      const scrollPosition = sidebarRef.current?.scrollTop || 0;
      
      // Navigate to the new category
      navigate(`/resources/${categoryId}`);
      
      // Restore scroll position after navigation
      // Use setTimeout to ensure this happens after render
      setTimeout(() => {
        if (sidebarRef.current) {
          sidebarRef.current.scrollTop = scrollPosition;
        }
      }, 0);
    }
  };
  
  return (
    <div className="lg:w-1/5">
      <div 
        ref={sidebarRef}
        className="sticky top-28 bg-white rounded-xl p-5 border border-[#e2dad3] shadow-sm max-h-[calc(100vh-140px)] overflow-y-auto"
      >
        <h3 className="font-bold text-base text-[#694e42] mb-3 pb-2 border-b border-[#e2dad3]">
          Resource Categories
        </h3>
        <ul className="space-y-1">
          {categories.map((cat) => (
            <li key={cat.id}>
              <a
                href={`/resources/${cat.id}`}
                onClick={(e) => handleCategoryClick(e, cat.id)}
                className={`flex items-center py-1.5 px-2 rounded-md text-sm transition-colors ${
                  cat.id === currentCategoryId
                    ? "bg-[#fff5eb] font-medium text-[#ff8c42]"
                    : "text-[#8a7266] hover:bg-[#f9f6f4] hover:text-[#694e42]"
                }`}
              >
                <div className="w-4 h-4 mr-2 flex-shrink-0 flex items-center justify-center text-current">
                  {cat.icon}
                </div>
                <span>{cat.title}</span>
              </a>
              
              {/* Show current category articles if this is the active category */}
              {cat.id === currentCategoryId && cat.articles.length > 0 && (
                <ul className="pl-4 mt-1 mb-2 space-y-0.5 border-l border-[#e2dad3] ml-2">
                  {cat.articles.map((article) => (
                    <li key={article.id}>
                      <a
                        href={`#${article.id}`}
                        className={`block py-1.5 px-2 text-xs rounded transition-colors ${
                          activeArticle === article.id
                            ? "text-[#ff8c42] font-medium bg-[#fff5eb]/50"
                            : "text-[#8a7266] hover:text-[#694e42] hover:bg-[#f9f6f4]"
                        }`}
                      >
                        {article.title}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default CategorySidebar;